<template>
    <div>
        <PageHeader>
            <h2>Upload documento</h2>
        </PageHeader>
        <el-form :model="form" label-width="auto" label-position="top" style="max-width: 600px" :disabled="isBusy">
            <el-form-item label="Oggetto" required>
                <el-input v-model="form.subject"/>
            </el-form-item>
            <el-form-item label="Codice fiscale" required>
<!--                <el-input v-model="form.fiscalCode" class="uppercase"/>-->
                <el-select v-model="form.fiscalCode" class="uppercase" placeholder="Seleziona un paziente" remote-show-suffix filterable remote clearable :remote-method="filterUsers" :loading="isFiltering"
                           :value-on-clear="null" :empty-values="[null]">
                    <el-option
                        v-for="user in users"
                        :key="user.fiscalCode"
                        :label="user.fiscalCode"
                        :value="user.fiscalCode">
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item label="Evento clinico" required>
                <el-input v-model="form.subReference"/>
            </el-form-item>
            <el-form-item label="Categoria documentale" required>
                <el-select v-model="form.categoryId" placeholder="Seleziona una categoria documentale" class="uppercase">
                    <el-option v-for="(category, index) in categories.filter((x: any) => x.selected)" :key="index"
                               :label="category.name" :value="category.id"/>
                </el-select>
            </el-form-item>
            <el-form-item label="Documento" required>
                <el-input v-model="(form.document ?? { name: '' }).name" readonly>
                    <template #append>
                        <el-button plain @click="loadDocument">{{
                                form.document === null ?
                                    "Carica..." : "Modifica..."
                            }}
                        </el-button>
                    </template>
                </el-input>
                <input id="input1" style="display: none;" type="file" @change="parseDocument"/>

<!--                <div style="display: flex; flex-direction: column; row-gap: 20px;">-->
<!--                    <el-button-group>-->
<!--                        <el-button @click="loadDocument">{{-->
<!--                                form.document === null ?-->
<!--                                    "Carica..." : "Modifica..."-->
<!--                            }}-->
<!--                        </el-button>-->
<!--                        <el-button v-if="form.document !== null" type="danger" plain-->
<!--                                   @click="form.document = null">Elimina-->
<!--                        </el-button>-->
<!--                    </el-button-group>-->
<!--                </div>-->
            </el-form-item>
            <el-form-item label="Note">
                <el-input v-model="form.notes"/>
            </el-form-item>
            <el-form-item>
                <el-button type="primary" @click="upload">Esegui upload</el-button>
            </el-form-item>
            <el-progress
                v-if="isUploading"
                :text-inside="true"
                :format="(percentage: number) => isIndeterminate ? '' : `${percentage}%`"
                :stroke-width="16"
                :percentage="uploadProgress"
                :indeterminate="isIndeterminate"/>
        </el-form>
    </div>
</template>

<script setup lang="ts">
import PageHeader from "@/components/PageHeader.vue";
import {onMounted, ref} from "vue";
import CategoryService from "@/services/CategoryService";
import {ElMessage} from "element-plus";
import Category from "@/types/Category";
import {useMainStore} from "@/stores/mainStore";
import DocumentService from "@/services/DocumentService";
import UserService from "@/services/UserService";
import User from "@/types/User";
import debounce from "lodash/debounce";

type FormData = {
    subject: string,
    fiscalCode: string,
    subReference: string,
    categoryId: number | null,
    document: File | null,
    notes: string
};

const store = useMainStore();
const form = ref<FormData>({
    subject: "",
    fiscalCode: "",
    subReference: "",
    categoryId: null,
    document: null,
    notes: ""
});
const categories = ref<Category[]>([]);
const isBusy = ref(false);
const isUploading = ref(false);
const isIndeterminate = ref(false);
const uploadProgress = ref(0);
const CHUNK_SIZE = 5 * 1024 * 1024 // 5MB
const users = ref<User[]>([]);
const isFiltering = ref(false);

onMounted(async () => {
    try {
        isBusy.value = true;
        const items = await CategoryService.getCategories(store.admin!.organization!.wlextHost, store.admin!.organization!.wlextOrganizationId);
        categories.value = items.map(x => ({
            ...x,
            availabilityDays: store.admin!.organization!.categories.find(y => y.name === x.name)?.availabilityDays ?? 45,
            conditions: store.admin!.organization!.categories.find(y => y.name === x.name)?.conditions ?? [],
            selected: store.admin!.organization!.categories.find(y => y.name === x.name) !== undefined
        }));
        if (categories.value.filter(x => x.selected).length === 1) {
            form.value.categoryId = categories.value.find(x => x.selected)!.id;
        }
    } catch (error: any) {
        ElMessage.error({message: error.message});
    } finally {
        isBusy.value = false;
    }
});

async function loadDocument() {
    document.getElementById('input1')!.click();
}

async function parseDocument(event: Event) {
    console.log((event.target as HTMLInputElement).files);
    form.value.document = (event.target as HTMLInputElement).files?.[0] ?? null;
}

async function upload() {
    if (form.value.document === null ||
        form.value.categoryId === null ||
        form.value.subject === "" ||
        form.value.fiscalCode === "" ||
        form.value.subReference === "") {
        ElMessage.error({ message: "Compilare tutti i campi obbligatori" });
        return;
    }

    try {
        const file: File = form.value.document;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        const id = crypto.randomUUID();

        uploadProgress.value = 0;
        isBusy.value = true;
        isUploading.value = true;
        isIndeterminate.value = false;

        for (let index = 0; index < totalChunks; index++) {
            const start = index * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);

            const blobChunk = file.slice(start, end);
            const arrayBuffer = await blobChunk.arrayBuffer();
            const chunk = arrayBufferToBase64(arrayBuffer);

            await DocumentService.uploadDocumentChunk(store.admin!.organization!.organizationId, id, index, totalChunks, form.value.subject, form.value.fiscalCode.toUpperCase(), form.value.subReference, form.value.categoryId!, chunk, form.value.notes);

            uploadProgress.value = Math.round(((index + 1) / totalChunks) * 100);

            if (index === totalChunks - 2) {
                isIndeterminate.value = true;
            }

            if (index === totalChunks - 1) {
                form.value = {
                    subject: "",
                    fiscalCode: "",
                    subReference: "",
                    categoryId: null,
                    document: null,
                    notes: ""
                };

                ElMessage.success({message: `Upload completato con successo`});
            }
        }
    } catch (error: any) {
        ElMessage.error({message: error.message});
    } finally {
        isBusy.value = false;
        isUploading.value = false;
    }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
}

async function getUsers() {
    isBusy.value = true;
    try {
        users.value = (await UserService.getUsers(1, store.admin!.organization!.id, false)).items;
    } catch (error: any) {
        ElMessage.error({ message: error.message });
    }
    isBusy.value = false;
}

let timer: number | null = null;

function filterUsers(query: string) {
    if (query && query.length >= 3) {
        isFiltering.value = true;

        if (timer)
            clearTimeout(timer);

        timer = setTimeout(async() => {
            try {
                users.value = (await UserService.getUsers(1, store.admin!.organization!.id, false, query)).items;
            } catch (error: any) {
                ElMessage.error({ message: error.message });
            } finally {
                isFiltering.value = false;
            }
        }, 300);
    } else {
        users.value = [];
    }
}
</script>

<style lang="scss">
    .uppercase * {
        text-transform: uppercase;
    }

    .el-input-group__append, .el-input-group__prepend {
        background-color: transparent !important;
    }
</style>